import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import {
  makeStyles, tokens,
  Button, Input, Badge, Spinner, Switch, RadioGroup, Radio,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Field, Link,
  useToastController, Toast, ToastTitle, ToastBody,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  ArrowEnterRegular,
  EditRegular,
  AddRegular,
  SearchRegular,
  PauseRegular,
  PlayRegular,
  DeleteRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAllTenants,
  createTenant,
  updateTenant,
  impersonateTenant,
  deleteTenant,
  type TenantRecord,
  type OtpMode,
} from '../api/tenants.api';
import { useAuthStore } from '../store/auth.store';
import { useTenantStore } from '../store/tenant.store';
import { useT } from '../i18n';
import { apiErrorMessage } from '../api/apiError';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { tenantHostUrl } from '../utils/host-tenant';
import type { AuthUser } from '../types/api.types';
import { TENANT_FEATURES, getDefaultPermissions } from '../constants/features';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const AVATAR_COLORS = [
  '#2e7d32', '#1565c0', '#6a1b9a', '#c62828',
  '#ef6c00', '#00695c', '#283593', '#558b2f',
];

function tenantAvatarColor(tenantName?: string): string {
  if (!tenantName) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < tenantName.length; i++) {
    hash = tenantName.charCodeAt(i) + (hash << 5) - hash;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles({
  page: {
    padding: '32px',
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    margin: 0,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  searchInput: {
    width: '300px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  thead: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  th: {
    padding: '10px 16px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tr: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '13.5px',
    color: tokens.colorNeutralForeground1,
    verticalAlign: 'middle',
  },
  logoCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoImg: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    objectFit: 'contain',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  logoPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '700',
    color: 'white',
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  code: {
    fontFamily: 'monospace',
    fontSize: '12px',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '2px 6px',
    borderRadius: '4px',
    color: tokens.colorNeutralForeground2,
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minWidth: '360px',
  },
});

// ---------------------------------------------------------------------------
// TenantFormDialog — shared for create + edit
// ---------------------------------------------------------------------------
interface TenantFormData {
  name: string;
  code: string;
  logoUrl: string;
  allowDemoLogin: boolean;
  otpMode: OtpMode;
  accessHoursEnabled: boolean;
  accessHours?: { start: string; end: string };
  passwordPolicyEnabled: boolean;
  passwordPolicy?: { minLength: number; maxAgeDays?: number };
  featureFlags: Record<string, boolean>;
}

interface TenantFormDialogProps {
  open: boolean;
  onClose: () => void;
  initial?: TenantRecord | null;
}

function TenantFormDialog({ open, onClose, initial }: TenantFormDialogProps) {
  const s = useStyles();
  const t = useT();
  const qc = useQueryClient();
  const { dispatchToast } = useToastController('agrovid-toaster');
  const isEdit = !!initial;
  const fe = useFieldErrors();

  const [form, setForm] = useState<TenantFormData>({
    name:    initial?.name    ?? '',
    code:    initial?.code    ?? '',
    logoUrl: initial?.logoUrl ?? '',
    allowDemoLogin: initial?.allowDemoLogin ?? false,
    // Sin otpMode (empresa anterior a esta opción) se deriva del requireOtp de siempre.
    otpMode: initial?.otpMode ?? (initial?.requireOtp === false ? 'NEVER' : 'REQUIRED'),
    accessHoursEnabled: !!initial?.accessHours,
    accessHours: initial?.accessHours ?? { start: '00:00', end: '23:59' },
    passwordPolicyEnabled: !!initial?.passwordPolicy,
    passwordPolicy: initial?.passwordPolicy ?? { minLength: 8 },
    featureFlags: initial?.featureFlags ?? getDefaultPermissions(),
  });

  useResetOnOpen(open, () => setForm({
    name:    initial?.name    ?? '',
    code:    initial?.code    ?? '',
    logoUrl: initial?.logoUrl ?? '',
    allowDemoLogin: initial?.allowDemoLogin ?? false,
    otpMode: initial?.otpMode ?? (initial?.requireOtp === false ? 'NEVER' : 'REQUIRED'),
    accessHoursEnabled: !!initial?.accessHours,
    accessHours: initial?.accessHours ?? { start: '00:00', end: '23:59' },
    passwordPolicyEnabled: !!initial?.passwordPolicy,
    passwordPolicy: initial?.passwordPolicy ?? { minLength: 8 },
    featureFlags: initial?.featureFlags ?? getDefaultPermissions(),
  }));

  const createMut = useMutation({
    mutationFn: () => createTenant({ name: form.name, code: form.code || undefined, logoUrl: form.logoUrl || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
      qc.invalidateQueries({ queryKey: ['user-tenants'] });
      dispatchToast(
        <Toast><ToastTitle>{t.superAdmin.tenantCreated}</ToastTitle></Toast>,
        { intent: 'success' },
      );
      onClose();
    },
    onError: () => dispatchToast(
      <Toast><ToastTitle>{t.superAdmin.tenantCreateError}</ToastTitle></Toast>,
      { intent: 'error' },
    ),
  });

  const updateMut = useMutation({
    mutationFn: () => updateTenant(initial!.tenantId, {
      name:    form.name    || undefined,
      code:    form.code    || undefined,
      logoUrl: form.logoUrl || undefined,
      allowDemoLogin: form.allowDemoLogin,
      otpMode: form.otpMode,
      accessHours: form.accessHoursEnabled ? form.accessHours : null,
      passwordPolicy: form.passwordPolicyEnabled ? form.passwordPolicy : null,
      featureFlags: Object.keys(form.featureFlags).length > 0 ? form.featureFlags : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
      qc.invalidateQueries({ queryKey: ['user-tenants'] });
      dispatchToast(
        <Toast><ToastTitle>{t.superAdmin.tenantUpdated}</ToastTitle></Toast>,
        { intent: 'success' },
      );
      onClose();
    },
    onError: () => dispatchToast(
      <Toast><ToastTitle>{t.superAdmin.tenantUpdateError}</ToastTitle></Toast>,
      { intent: 'error' },
    ),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const handleSubmit = () => {
    if (!fe.validate({ name: !form.name.trim() && t.common.validation.nameRequired })) return;
    isEdit ? updateMut.mutate() : createMut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface>
        <DialogTitle>{isEdit ? t.superAdmin.editTenant : t.superAdmin.newTenant}</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div className={s.formGrid}>
              <Field label={t.superAdmin.fieldName} required {...fe.props('name')}>
                <Input
                  value={form.name}
                  onChange={(_, d) => { setForm(f => ({ ...f, name: d.value })); fe.clear('name'); }}
                  placeholder={t.superAdmin.fieldNamePlaceholder}
                  disabled={saving}
                />
              </Field>
              <Field label={t.superAdmin.fieldCode} hint={t.superAdmin.fieldCodeHint}>
                <Input
                  value={form.code}
                  onChange={(_, d) => setForm(f => ({ ...f, code: d.value.toLowerCase().replace(/\s+/g, '-') }))}
                  placeholder={t.superAdmin.fieldCodePlaceholder}
                  disabled={saving}
                  contentBefore={<span style={{ color: tokens.colorNeutralForeground3, fontSize: 13 }}>/</span>}
                />
              </Field>
              <Field label={t.superAdmin.fieldLogo} hint={t.superAdmin.fieldLogoHint}>
                <Input
                  value={form.logoUrl}
                  onChange={(_, d) => setForm(f => ({ ...f, logoUrl: d.value }))}
                  placeholder={t.superAdmin.fieldLogoPlaceholder}
                  disabled={saving}
                />
              </Field>
              {form.logoUrl && (
                <img
                  src={form.logoUrl}
                  alt={t.a11yLabels.logoPreview}
                  className={s.logoImg}
                  style={{ width: 64, height: 64 }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <Field label={t.superAdmin.demoAccessTitle}>
                <Switch
                  checked={form.allowDemoLogin}
                  onChange={(_, d) => setForm(f => ({ ...f, allowDemoLogin: d.checked }))}
                  label={form.allowDemoLogin ? t.superAdmin.demoAccessEnabled : t.superAdmin.demoAccessDisabled}
                  disabled={saving}
                />
              </Field>
              <Field label={t.superAdmin.otpTitle} hint={t.superAdmin.otpHint}>
                <RadioGroup
                  value={form.otpMode}
                  onChange={(_, d) => setForm(f => ({ ...f, otpMode: d.value as OtpMode }))}
                  disabled={saving}
                >
                  <Radio value="REQUIRED" label={t.superAdmin.otpModeRequired} />
                  <Radio value="IF_LINKED" label={t.superAdmin.otpModeIfLinked} />
                  <Radio value="NEVER" label={t.superAdmin.otpModeNever} />
                </RadioGroup>
              </Field>

              {/* Access Hours */}
              <Field label={t.superAdmin.accessHoursTitle} hint={t.superAdmin.accessHoursHint}>
                <Switch
                  checked={form.accessHoursEnabled}
                  onChange={(_, d) => setForm(f => ({ ...f, accessHoursEnabled: d.checked }))}
                  label={form.accessHoursEnabled ? t.superAdmin.enabled : t.superAdmin.disabled}
                  disabled={saving}
                />
              </Field>
              {form.accessHoursEnabled && form.accessHours && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Field label={t.superAdmin.accessHoursStart} style={{ flex: 1 }}>
                    <Input
                      value={form.accessHours.start}
                      onChange={(_, d) => setForm(f => ({ ...f, accessHours: { ...f.accessHours!, start: d.value } }))}
                      placeholder="00:00"
                      disabled={saving}
                      type="time"
                    />
                  </Field>
                  <Field label={t.superAdmin.accessHoursEnd} style={{ flex: 1 }}>
                    <Input
                      value={form.accessHours.end}
                      onChange={(_, d) => setForm(f => ({ ...f, accessHours: { ...f.accessHours!, end: d.value } }))}
                      placeholder="23:59"
                      disabled={saving}
                      type="time"
                    />
                  </Field>
                </div>
              )}

              {/* Password Policy */}
              <Field label={t.superAdmin.passwordPolicyTitle} hint={t.superAdmin.passwordPolicyHint}>
                <Switch
                  checked={form.passwordPolicyEnabled}
                  onChange={(_, d) => setForm(f => ({ ...f, passwordPolicyEnabled: d.checked }))}
                  label={form.passwordPolicyEnabled ? t.superAdmin.enabled : t.superAdmin.disabled}
                  disabled={saving}
                />
              </Field>
              {form.passwordPolicyEnabled && form.passwordPolicy && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Field label={t.superAdmin.passwordMinLength} style={{ flex: 1 }}>
                    <Input
                      value={String(form.passwordPolicy.minLength)}
                      onChange={(_, d) => {
                        const val = parseInt(d.value) || 1;
                        setForm(f => ({ ...f, passwordPolicy: { ...f.passwordPolicy!, minLength: Math.max(1, val) } }));
                      }}
                      placeholder={t.superAdmin.passwordMinLengthPlaceholder}
                      disabled={saving}
                      type="number"
                      min="1"
                      max="64"
                    />
                  </Field>
                  <Field label={t.superAdmin.passwordMaxAgeDays} style={{ flex: 1 }}>
                    <Input
                      value={form.passwordPolicy.maxAgeDays ? String(form.passwordPolicy.maxAgeDays) : ''}
                      onChange={(_, d) => {
                        const val = d.value ? parseInt(d.value) : undefined;
                        setForm(f => ({ ...f, passwordPolicy: { ...f.passwordPolicy!, maxAgeDays: val } }));
                      }}
                      placeholder={t.superAdmin.passwordMaxAgeDaysPlaceholder}
                      disabled={saving}
                      type="number"
                      min="1"
                      max="365"
                    />
                  </Field>
                </div>
              )}

              {/* Feature permissions — checkboxes */}
              <Field label={t.superAdmin.featureFlagsTitle} hint={t.superAdmin.featureFlagsHint}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '12px',
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: tokens.colorNeutralBackground2,
                  borderRadius: '6px',
                  border: `1px solid ${tokens.colorNeutralStroke2}`,
                }}>
                  {Object.values(TENANT_FEATURES).map(feature => (
                    <label
                      key={feature.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() => {
                        if (!saving) {
                          const newValue = !(form.featureFlags[feature.key] ?? feature.default);
                          setForm(f => ({
                            ...f,
                            featureFlags: {
                              ...f.featureFlags,
                              [feature.key]: newValue,
                            },
                          }));
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.featureFlags[feature.key] ?? feature.default}
                        onChange={(e) => {
                          const value = e.target.checked;
                          setForm(f => ({
                            ...f,
                            featureFlags: {
                              ...f.featureFlags,
                              [feature.key]: value,
                            },
                          }));
                        }}
                        disabled={saving}
                        style={{ cursor: saving ? 'not-allowed' : 'pointer' }}
                      />
                      <span>{(t.superAdmin.featureLabels as Record<string, string>)[feature.key] ?? feature.label}</span>
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>{t.superAdmin.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={saving}
              icon={saving ? <Spinner size="tiny" /> : undefined}
            >
              {isEdit ? t.superAdmin.saveChanges : t.superAdmin.createTenant}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SuperAdminPage
// ---------------------------------------------------------------------------
export function SuperAdminPage() {
  const s = useStyles();
  const t = useT();
  const { formatDate } = useLocaleSettings();
  const { user, impersonate } = useAuthStore();
  const { tenantId: activeTenantId, setTenant } = useTenantStore();
  const isChangingTenant = !!activeTenantId;
  const { dispatchToast, dismissToast } = useToastController('agrovid-toaster');
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TenantRecord | null>(null);
  const [deactivating, setDeactivating] = useState<TenantRecord | null>(null);
  const [deletingTenant, setDeletingTenant] = useState<TenantRecord | null>(null);

  // Guard: only SUPER_ADMIN
  if (!user?.roles.includes('SUPER_ADMIN')) {
    return (
      <div className={s.emptyState}>
        <p>{t.superAdmin.accessDenied}</p>
      </div>
    );
  }

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['super-admin-tenants'],
    queryFn:  fetchAllTenants,
  });

  const impersonateMut = useMutation({
    mutationFn: (tenantId: string) => impersonateTenant(tenantId),
    onSuccess: (data, tenantId) => {
      const code = (data.tenant.code ?? tenantId).replace(/^\//, '');
      // Bajo el dominio propio cada empresa tiene su subdominio y la sesión no se comparte.
      const hostUrl = tenantHostUrl(code, '/');
      if (hostUrl) {
        window.location.assign(hostUrl);
        return;
      }
      const tenantUser: AuthUser = {
        phoneNumber: user?.phoneNumber ?? '',
        name: user?.name,
        roles: user?.roles ?? [],
        activeRole: 'SUPER_ADMIN',
        tenantId,
        tenantCode: code,
        tenantName: data.tenant.name,
        tenantLogoUrl: data.tenant.logoUrl,
      };
      impersonate(data.token, tenantUser);
      setTenant(code, data.tenant.name, data.tenant.logoUrl ?? undefined);
      // Recarga completa: ver la nota en TopBar.handleSwitchTenant (evita el rebote a /login).
      window.location.assign(`/${code}/dashboard`);
    },
    onError: (err) => dispatchToast(
      <Toast>
        <ToastTitle>{t.superAdmin.loginError}</ToastTitle>
        <ToastBody>{apiErrorMessage(err, t.superAdmin.loginErrorBody)}</ToastBody>
      </Toast>,
      { intent: 'error' },
    ),
  });

  const filtered = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(search.toLowerCase()) ||
    (tenant.code ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit   = (tenant: TenantRecord) => { setEditing(tenant); setFormOpen(true); };

  const statusMut = useMutation({
    mutationFn: (tenant: TenantRecord) =>
      updateTenant(tenant.tenantId, { status: tenant.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }),
    onSuccess: (_data, tenant) => {
      qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
      qc.invalidateQueries({ queryKey: ['user-tenants'] });
      dispatchToast(
        <Toast><ToastTitle>{tenant.status === 'ACTIVE' ? t.superAdmin.tenantDeactivated : t.superAdmin.tenantActivated}</ToastTitle></Toast>,
        { intent: 'success' },
      );
      setDeactivating(null);
    },
    onError: (err, tenant) => {
      const reason = apiErrorMessage(err);
      const needsCode = tenant.status !== 'ACTIVE' && !(tenant.code ?? '').trim();
      dispatchToast(
        <Toast>
          <ToastTitle
            action={needsCode ? (
              <Link onClick={() => { dismissToast('status-needs-code'); openEdit(tenant); }}>
                {t.superAdmin.edit}
              </Link>
            ) : undefined}
          >
            {t.superAdmin.statusChangeError}
          </ToastTitle>
          <ToastBody>{needsCode ? t.superAdmin.activateNeedsCode : reason}</ToastBody>
        </Toast>,
        { intent: 'error', toastId: 'status-needs-code' },
      );
    },
  });

  const deleteMut = useMutation({
    mutationFn: (tenant: TenantRecord) => deleteTenant(tenant.tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
      qc.invalidateQueries({ queryKey: ['user-tenants'] });
      dispatchToast(
        <Toast><ToastTitle>{t.superAdmin.tenantDeleted}</ToastTitle></Toast>,
        { intent: 'success' },
      );
      setDeletingTenant(null);
    },
    onError: () => dispatchToast(
      <Toast><ToastTitle>{t.superAdmin.tenantDeleteError}</ToastTitle></Toast>,
      { intent: 'error' },
    ),
  });

  return (
    <div className={s.page}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <h1 className={s.title}>{t.superAdmin.title}</h1>
          <p className={s.subtitle}>
            {tenants.length} {tenants.length !== 1 ? t.superAdmin.tenantsRegisteredPlural : t.superAdmin.tenantsRegisteredSingular}
          </p>
        </div>
        <Button
          appearance="primary"
          icon={<AddRegular />}
          onClick={openCreate}
        >
          {t.superAdmin.newTenantBtn}
        </Button>
      </div>

      {/* Toolbar */}
      <div className={s.toolbar}>
        <Input
          className={s.searchInput}
          placeholder={t.superAdmin.searchPlaceholder}
          value={search}
          onChange={(_, d) => setSearch(d.value)}
          contentBefore={<SearchRegular />}
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className={s.emptyState}><Spinner label={t.superAdmin.loadingTenants} /></div>
      ) : filtered.length === 0 ? (
        <div className={s.emptyState}>
          <BuildingRegular style={{ fontSize: 48, opacity: 0.3 }} />
          <p>{search ? t.superAdmin.noResults : t.superAdmin.noTenants}</p>
        </div>
      ) : (
        <table className={s.table}>
          <thead className={s.thead}>
            <tr>
              <th className={s.th}>{t.superAdmin.colCompany}</th>
              <th className={s.th}>{t.superAdmin.colCode}</th>
              <th className={s.th}>{t.superAdmin.colStatus}</th>
              <th className={s.th}>{t.superAdmin.colCreated}</th>
              <th className={s.th}>{t.superAdmin.colActions}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(tenant => (
              <tr key={tenant.tenantId} className={s.tr}>
                {/* Empresa */}
                <td className={s.td}>
                  <div className={s.logoCell}>
                    {tenant.logoUrl ? (
                      <img
                        src={tenant.logoUrl}
                        alt={tenant.name}
                        className={s.logoImg}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div
                        className={s.logoPlaceholder}
                        style={{ backgroundColor: tenantAvatarColor(tenant.name) }}
                      >
                        {tenant.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span style={{ fontWeight: 600 }}>{tenant.name}</span>
                  </div>
                </td>

                {/* Código */}
                <td className={s.td}>
                  {tenant.code ? <span className={s.code}>/{tenant.code}</span> : <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>}
                </td>

                {/* Estado */}
                <td className={s.td}>
                  <Badge
                    appearance="filled"
                    color={tenant.status === 'ACTIVE' ? 'success' : 'warning'}
                    size="small"
                  >
                    {tenant.status === 'ACTIVE' ? t.superAdmin.statusActive : t.superAdmin.statusInactive}
                  </Badge>
                </td>

                {/* Fecha */}
                <td className={s.td} style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>
                  {tenant.createdAt ? formatDate(tenant.createdAt) : '—'}
                </td>

                {/* Acciones */}
                <td className={s.td}>
                  <div className={s.actions}>
                    <Button
                      size="small"
                      appearance="primary"
                      icon={<ArrowEnterRegular />}
                      onClick={() => impersonateMut.mutate(tenant.tenantId)}
                      disabled={impersonateMut.isPending || tenant.status !== 'ACTIVE' || tenant.tenantId === user?.tenantId}
                      title={tenant.tenantId === user?.tenantId
                        ? t.superAdmin.currentTenantHint
                        : tenant.status !== 'ACTIVE' ? t.superAdmin.activateToLogin : undefined}
                    >
                      {tenant.tenantId === user?.tenantId
                        ? t.superAdmin.currentBtn
                        : isChangingTenant ? t.superAdmin.changeBtn : t.superAdmin.login}
                    </Button>
                    <Button
                      size="small"
                      appearance="outline"
                      icon={<EditRegular />}
                      onClick={() => openEdit(tenant)}
                    >
                      {t.superAdmin.edit}
                    </Button>
                    {tenant.status === 'ACTIVE' ? (
                      <Button
                        size="small"
                        appearance="outline"
                        icon={<PauseRegular />}
                        onClick={() => setDeactivating(tenant)}
                      >
                        {t.superAdmin.deactivate}
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="small"
                          appearance="outline"
                          icon={<PlayRegular />}
                          onClick={() => statusMut.mutate(tenant)}
                          disabled={statusMut.isPending}
                        >
                          {t.superAdmin.activate}
                        </Button>
                        <Button
                          size="small"
                          appearance="outline"
                          icon={<DeleteRegular />}
                          style={{ color: tokens.colorStatusDangerForeground1 }}
                          onClick={() => setDeletingTenant(tenant)}
                          disabled={deleteMut.isPending}
                        >
                          {t.common.delete}
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create / Edit dialog — key forces remount so useState reloads initial data */}
      <TenantFormDialog
        key={editing?.tenantId ?? 'new'}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initial={editing}
      />

      {/* Confirm deactivation — blocks login for all users of the tenant, doesn't delete data */}
      <Dialog open={!!deactivating} onOpenChange={(_, d) => !d.open && setDeactivating(null)} modalType="modal">
        <DialogSurface>
          <DialogTitle>{t.superAdmin.deactivateTitle}</DialogTitle>
          <DialogBody>
            <DialogContent>
              <p>
                <strong>{deactivating?.name}</strong> {t.superAdmin.deactivateBodySuffix}
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeactivating(null)} disabled={statusMut.isPending}>
                {t.superAdmin.cancel}
              </Button>
              <Button
                appearance="primary"
                onClick={() => deactivating && statusMut.mutate(deactivating)}
                disabled={statusMut.isPending}
                icon={statusMut.isPending ? <Spinner size="tiny" /> : undefined}
              >
                {t.superAdmin.deactivate}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Confirm deletion — soft delete, solo permitido si el tenant ya está inactivo */}
      <Dialog open={!!deletingTenant} onOpenChange={(_, d) => !d.open && setDeletingTenant(null)} modalType="modal">
        <DialogSurface>
          <DialogTitle>{t.superAdmin.deleteTenantTitle}</DialogTitle>
          <DialogBody>
            <DialogContent>
              <p>
                {t.superAdmin.deleteTenantBody.split('{name}')[0]}<strong>{deletingTenant?.name}</strong>{t.superAdmin.deleteTenantBody.split('{name}')[1]}
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeletingTenant(null)} disabled={deleteMut.isPending}>
                {t.superAdmin.cancel}
              </Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: tokens.colorStatusDangerBackground3 }}
                onClick={() => deletingTenant && deleteMut.mutate(deletingTenant)}
                disabled={deleteMut.isPending}
                icon={deleteMut.isPending ? <Spinner size="tiny" /> : undefined}
              >
                {t.common.delete}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
